import React, { createContext, useContext, useState, ReactNode } from 'react'

type Language = 'zh' | 'en'

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  t: (key: string) => string
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

interface LanguageProviderProps {
  children: ReactNode
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('zh')

  const t = (key: string): string => {
    return translations[language][key] || key
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  )
}

export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}

// 翻译数据
const translations: Record<Language, Record<string, string>> = {
  zh: {
    // Navigation
    'nav.home': '首页',
    'nav.about': '关于我们',
    'nav.vision': '公司愿景',
    'nav.mission': '公司使命',
    'nav.innovation': '关键创新',
    'nav.products': '主要产品',
    'nav.scenarios': '应用场景',
    'nav.careers': '加入我们',
    'nav.contact': '联系我们',

    // Home Page
    'home.hero.badge': '面向未来的智能汽车开发平台',
    'home.hero.title': 'xDT',
    'home.hero.subtitle': '汽车行业的 GitHub',
    'home.hero.description': '面向未来智能电动汽车的协同开发平台',
    'home.hero.detail': '我们通过云服务与数字孪生技术，打造开放、共创的集成开发环境，助力全球开发者高效构建下一代智能电动汽车。',
    'home.hero.cta1': '探索我们的平台',
    'home.hero.cta2': '了解我们的产品',
    'home.hero.cta3': '联系我们',

    // Home hero visual elements
    'home.hero.visual.title1': '整车虚拟化开发',
    'home.hero.visual.desc1': '基于L4级芯片行为模型的完整虚拟化解决方案',
    'home.hero.visual.title2': '自动化设计',
    'home.hero.visual.desc2': '智能生成式设计工具，提升开发效率80%',
    'home.hero.visual.title3': '云原生平台',
    'home.hero.visual.desc3': '支持CI/CD的云端集成开发环境',

    'home.features.title': '为什么选择 xDT？',
    'home.features.subtitle': '我们提供业界领先的智能电动汽车开发解决方案，助力您的团队实现突破性创新',

    'home.feature.simulation.title': '高精度仿真模型',
    'home.feature.simulation.desc': '基于L4级芯片行为模型，支持ARM、RISC-V等主流架构，实现真实场景下的控制器和整车仿真。',

    'home.feature.collaboration.title': '开放协同平台',
    'home.feature.collaboration.desc': '如GitHub般的开放生态，支持全球开发者在统一平台上协作创新，推动知识共享。',

    'home.feature.ai.title': '自动化设计',
    'home.feature.ai.desc': '集成自动化设计与汽车子系统知识，实现生成式设计与优化，开发时间缩短80%。',

    'home.feature.cloud.title': '云原生开发',
    'home.feature.cloud.desc': '提供云端集成开发环境，支持CI/CD流程，实现开发左移与自动化率提升。',

    'home.feature.vehicle.title': '整车级支持',
    'home.feature.vehicle.desc': '从芯片到整车的完整工具链，覆盖电子电气架构设计、仿真、测试全流程。',

    'home.feature.industrial.title': '工业级可靠',
    'home.feature.industrial.desc': '经过车企验证的成熟解决方案，满足汽车行业严格的质量与安全标准。',

    // Home CTA section
    'home.cta.title': '准备开始您的智能汽车开发之旅？',
    'home.cta.subtitle': '加入xDT，与全球开发者一起构建未来汽车',
    'home.cta.cta1': '探索我们的产品',
    'home.cta.cta2': '观看产品演示',

    // Company info
    'company.name': 'xDT',
    'company.tagline': '汽车行业的GitHub',
    'company.description': '汽车行业的GitHub，面向未来智能电动汽车的协同开发平台。通过云服务与数字孪生技术，打造开放、共创的集成开发环境。',
    'company.location': '中国 · 上海徐汇',
    'company.email': 'contact@xdt.com',
    'company.copyright': '保留所有权利。',
    'company.slogan': '工业软件 · 汽车行业的GitHub · 智能电动汽车协同开发平台',

    // Footer
    'footer.company': '公司',
    'footer.products': '产品',
    'footer.resources': '开发者资源',
    'footer.legal': '法律信息',

    // About Page
    'about.hero.title': '关于 xDT',
    'about.hero.subtitle': '汽车行业的GitHub',
    'about.hero.description': '我们致力于成为汽车行业的GitHub，为智能电动汽车开发者提供高效的协同开发解决方案',
    
    'about.intro.title': '公司简介',
    'about.intro.positioning': '行业定位：',
    'about.intro.positioning.desc': 'xDT专注于工业软件领域，为智能电动汽车行业提供创新的技术解决方案。',
    'about.intro.purpose': '成立初衷：',
    'about.intro.purpose.desc': '随着汽车产业向"软件定义汽车"转型，我们深刻认识到行业需要一个开放、协同的开发平台，就像GitHub改变了软件开发一样，我们希望为汽车行业带来同样的变革。',
    'about.intro.goal': '发展目标：',
    'about.intro.goal.desc': '成为汽车行业的GitHub，构建一个全球化的开发者生态系统，推动智能汽车软件创新，让每一个开发者都能在我们的平台上高效协作，共同构建未来汽车。',
    
    'about.tech.title': '技术背景',
    'about.tech.intro': '我们的核心技术基于L4级芯片行为模型的高精度仿真系统，这是业界领先的技术创新。通过深度整合数字孪生、云原生、自动化工具链等前沿技术，我们为汽车开发者提供了前所未有的开发体验。',
    'about.tech.digital.title': '数字孪生技术',
    'about.tech.digital.desc': '构建高保真的虚拟车辆模型，实现物理世界与数字世界的精确映射，支持全生命周期的仿真验证。',
    'about.tech.cloud.title': '云原生架构',
    'about.tech.cloud.desc': '基于Kubernetes的弹性架构，支持大规模并发开发，提供随时随地的开发环境访问。',
    'about.tech.automation.title': '自动化工具链',
    'about.tech.automation.desc': '从代码提交到部署的全自动化流程，支持CI/CD，大幅提升开发效率和代码质量。',
    'about.tech.ai.title': '自动化设计优化',
    'about.tech.ai.desc': '集成机器学习算法，提供智能化的设计建议和性能优化方案，加速创新过程。',
    
    'about.team.title': '团队介绍',
    'about.team.intro': '我们的核心团队由来自汽车行业和软件领域的资深专家组成，拥有丰富的跨界经验和深厚的技术积累。',
    'about.team.tech.title': '技术专家',
    'about.team.tech.desc': '深耕汽车电子、芯片设计、系统架构等核心技术领域',
    'about.team.product.title': '产品专家',
    'about.team.product.desc': '具备丰富的B2B产品设计和用户体验优化经验',
    'about.team.industry.title': '行业专家',
    'about.team.industry.desc': '来自领先车企和Tier1供应商的资深工程师',
    
    'about.features.industrial': '工业软件',
    'about.features.industrial.desc': '专业领域',
    'about.features.smart': '智能汽车',
    'about.features.smart.desc': '服务对象',
    'about.features.collaboration': '协同开发',
    'about.features.collaboration.desc': '核心理念',
    'about.features.innovation': '创新引领',
    'about.features.innovation.desc': '发展方向',

    // Products Page
    'products.hero.title': '我们的产品',
    'products.hero.subtitle': '完整的智能电动汽车开发工具链',
    'products.hero.description': '从芯片级仿真到整车开发，我们提供全方位的解决方案，助力您构建下一代智能电动汽车。',

    'products.onesoul.title': 'One Soul 平台',
    'products.onesoul.subtitle': '整车电子电气架构虚拟化开发平台',
    'products.onesoul.badge': '✅ 平台类产品',
    'products.onesoul.features.title': '功能亮点：',
    'products.onesoul.features.1': 'L4级芯片行为模型库（覆盖ARM/RISC-V等）',
    'products.onesoul.features.2': '整车EE架构参考设计与模型库',
    'products.onesoul.features.3': '支持自研/第三方工具集成',
    'products.onesoul.features.4': '与CI/CD流程无缝对接',
    'products.onesoul.scenarios.title': '应用场景：',
    'products.onesoul.scenarios.1': '架构选型验证',
    'products.onesoul.scenarios.2': '虚拟化开发与测试',
    'products.onesoul.scenarios.3': '自动化回归测试',
    
    'products.onebody.title': 'One Body 平台', 
    'products.onebody.subtitle': '自动化汽车设计平台',
    'products.onebody.badge': '✅ 平台类产品',
    'products.onebody.features.title': '功能亮点：',
    'products.onebody.features.1': '基于自动化设计的生成式设计与优化工具',
    'products.onebody.features.2': '领域知识库+训练模型支持轻量化设计',
    'products.onebody.features.3': '轻量IDE兼容主流CAE软件（如CATIA, Siemens NX等）',
    'products.onebody.scenarios.title': '应用场景：',
    'products.onebody.scenarios.1': '线束布线优化',
    'products.onebody.scenarios.2': '开闭件快速设计',
    'products.onebody.scenarios.3': '结构性能优化',
    
    // Products Visual Cards
    'products.onesoul.visual.title': 'L4级芯片仿真',
    'products.onesoul.visual.subtitle': '支持ARM、RISC-V等主流架构的高精度行为级仿真',
    'products.onesoul.visual.arch': '架构支持',
    'products.onesoul.visual.arch.value': 'ARM/RISC-V',
    'products.onesoul.visual.os': '操作系统',
    'products.onesoul.visual.os.value': 'POSIX兼容',
    
    'products.onebody.visual.title': '自动化设计',
    'products.onebody.visual.subtitle': '基于自动化设计的生成式设计工具，开发效率提升80%',
    'products.onebody.visual.wiring': '线束布线优化',
    'products.onebody.visual.wiring.desc': '自动生成最优路径',
    'products.onebody.visual.design': '开闭件设计',
    'products.onebody.visual.design.desc': '快速生成数模',
    
    // Products CTA
    'products.cta.title': '想要体验我们的产品？',
    'products.cta.subtitle': '联系我们获取产品演示和技术支持',
    'products.cta.contact': '联系我们',
    'products.cta.demo': '预约演示',
    
    'products.toolchain.badge': '工具链软件（Toolchain）',
    'products.toolchain.title': '完整的工具链生态',
    'products.toolchain.subtitle': '从芯片模型到整车设计的完整工具链，覆盖智能电动汽车开发全流程',
    'products.toolchain.table.name': '工具名称',
    'products.toolchain.table.description': '功能说明',
    
    // Toolchain tools
    'products.tools.1.name': 'L4级芯片行为模型建模工具',
    'products.tools.1.desc': '实现高精度芯片级仿真',
    'products.tools.2.name': 'L4级芯片行为模型库',
    'products.tools.2.desc': '覆盖主流芯片架构的模型资源',
    'products.tools.3.name': '参考SWC软件库',
    'products.tools.3.desc': '标准化软件组件库',
    'products.tools.4.name': 'Autocoding 工具',
    'products.tools.4.desc': '实现跨芯片平台自动适配与编译',
    'products.tools.5.name': '控制器功耗仿真工具',
    'products.tools.5.desc': '模拟真实使用场景下的功耗表现',
    'products.tools.6.name': '汽车线束3D自动化布线软件',
    'products.tools.6.desc': '自动生成整车线束最优路径与数模',
    'products.tools.7.name': '汽车开闭件自动化设计软件',
    'products.tools.7.desc': '基于A面快速生成符合工程标准的开闭件数模',
    'products.tools.8.name': '白车身上车体自动设计软件',
    'products.tools.8.desc': '基于A面快速生成符合工程标准的白车身上车身数模',
    'products.tools.9.name': '电池包自动化设计软件',
    'products.tools.9.desc': '自动化生成电池包设计和数模',

    // Use Cases Section
    'products.usecases.title': '应用场景',
    'products.usecases.subtitle': '真实客户案例展示平台价值',
    'products.usecases.oem.title': '车企如何利用One Soul平台进行早期架构验证',
    'products.usecases.oem.desc': '通过L4级芯片行为模型，车企可以在硬件投产前完成完整的EE架构验证，降低开发风险，缩短研发周期30%以上。',
    'products.usecases.oem.benefits.1': '提前发现架构设计问题',
    'products.usecases.oem.benefits.2': '降低硬件开发成本',
    'products.usecases.oem.benefits.3': '加速产品上市时间',
    
    'products.usecases.tier1.title': 'Tier1供应商如何借助One Body平台加速零部件设计迭代',
    'products.usecases.tier1.desc': '基于自动化设计工具，Tier1供应商能够快速生成多种设计方案，实现设计迭代效率提升80%，同时保证工程标准合规性。',
    'products.usecases.tier1.benefits.1': '设计效率提升80%',
    'products.usecases.tier1.benefits.2': '自动化质量检查',
    'products.usecases.tier1.benefits.3': '多方案快速对比',
    
    'products.usecases.autonomous.title': '自动驾驶公司如何整合我们的工具链实现快速部署',
    'products.usecases.autonomous.desc': '通过完整的工具链生态，自动驾驶公司可以从芯片级仿真到整车验证的全流程开发，实现CI/CD自动化部署，大幅提升开发效率。',
    'products.usecases.autonomous.benefits.1': '全流程自动化开发',
    'products.usecases.autonomous.benefits.2': 'CI/CD集成部署',
    'products.usecases.autonomous.benefits.3': '跨平台兼容性验证',

    // Scenarios Page - Case Studies
    'scenarios.casestudies.title': '客户成功案例',
    'scenarios.casestudies.subtitle': '真实案例展示我们平台如何帮助不同类型的客户解决实际业务挑战',
    'scenarios.oem.badge': '车企案例',
    'scenarios.oem.case.title': '某头部车企EE架构验证项目',
    'scenarios.oem.case.desc': '该车企在新一代智能汽车开发中，采用我们的One Soul平台进行EE架构早期验证，成功在硬件投产前发现并解决了12个关键架构问题，避免了超过2000万元的潜在损失。',
    'scenarios.oem.metrics.cycle': '研发周期缩短',
    'scenarios.oem.metrics.issues': '提前发现问题',
    'scenarios.oem.progress.title': '项目进度',
    'scenarios.oem.progress.ecu': 'ECU节点',
    'scenarios.oem.progress.tests': '测试用例',
    
    'scenarios.tier1.badge': 'Tier1案例',
    'scenarios.tier1.case.title': '某线束供应商设计优化项目',
    'scenarios.tier1.case.desc': '该Tier1供应商使用我们的One Body平台，在某豪华品牌车型的线束设计项目中，将原本需要30天的设计周期缩短至6天，同时生成了5种不同的优化方案供客户选择。',
    'scenarios.tier1.metrics.efficiency': '设计效率提升',
    'scenarios.tier1.traditional': '传统设计',
    'scenarios.tier1.ai': '自动化设计',
    'scenarios.tier1.benefits.routing': '自动化布线路径优化',
    'scenarios.tier1.benefits.comparison': '多方案快速生成与对比',
    'scenarios.tier1.benefits.validation': '工程标准自动验证',
    
    'scenarios.cta.title': '想了解更多应用场景？',
    'scenarios.cta.subtitle': '联系我们的专家团队，获取详细的案例分析和定制化解决方案',
    'scenarios.cta.contact': '联系专家',
    'scenarios.cta.products': '了解产品',

    // Vision Page
    'vision.hero.title': '公司愿景',
    'vision.hero.subtitle': '成为汽车行业的GitHub',
    'vision.content': '我们的愿景是成为汽车行业的GitHub，打造全球最大的智能电动汽车开源协作平台。',

    // Vision Goals Section
    'vision.goals.title': '具体目标',
    'vision.goals.subtitle': '通过三个核心目标，实现我们成为汽车行业GitHub的愿景',
    
    'vision.goals.ecosystem.title': '构建开放、透明、共享的开发生态',
    'vision.goals.ecosystem.desc': '打造一个像GitHub一样开放透明的平台，让全球汽车开发者能够自由分享代码、工具和知识，促进整个行业的协作创新。',
    
    'vision.goals.collaboration.title': '支持全球开发者在统一平台上协作创新',
    'vision.goals.collaboration.desc': '提供统一的开发环境和协作工具，让来自不同国家、不同公司的开发者能够无缝协作，共同推进智能汽车技术的发展。',
    
    'vision.goals.transformation.title': '推动汽车产业从"硬件驱动"向"软件定义"转型',
    'vision.goals.transformation.desc': '通过提供先进的软件开发工具和平台，加速汽车行业向软件定义汽车的转型，让软件成为汽车创新的核心驱动力。',

    // Vision Impact Section
    'vision.impact.title': '愿景实现的影响',
    'vision.impact.subtitle': '当我们的愿景实现时，将为整个汽车行业带来深远的变革',
    
    'vision.impact.developers.title': '对开发者的影响',
    'vision.impact.developers.1': '降低汽车软件开发门槛，让更多开发者参与创新',
    'vision.impact.developers.2': '提供丰富的开源工具和资源库，加速开发进程',
    'vision.impact.developers.3': '建立全球化的开发者社区，促进知识分享和学习',
    
    'vision.impact.industry.title': '对行业的影响',
    'vision.impact.industry.1': '加速整个汽车行业的数字化转型进程',
    'vision.impact.industry.2': '推动标准化和互操作性，减少重复开发',
    'vision.impact.industry.3': '促进创新合作，缩短产品上市时间',

    // Mission Page
    'mission.hero.title': '公司使命',
    'mission.hero.subtitle': '推动汽车行业数字化转型',
    'mission.content': '通过提供先进的开发工具和平台，推动汽车行业向智能化、电动化转型。',
    'mission.our.title': '我们的使命',
    'mission.our.desc': '推动汽车行业数字化转型，让每个开发者都能享受到优质的开发工具和平台服务。',
    'mission.goals.1': '降低汽车开发门槛，让更多创新者参与到智能汽车的发展中来',
    'mission.goals.2': '提供世界级的开发工具和平台，加速汽车行业技术进步',
    'mission.goals.3': '构建开放协作的生态系统，促进行业内外的知识共享与合作',
    
    'mission.values.title': '核心价值观',
    'mission.values.innovation': '创新驱动',
    'mission.values.innovation.desc': '持续创新，引领技术发展趋势',
    'mission.values.quality': '品质至上',  
    'mission.values.quality.desc': '追求极致的产品品质和用户体验',
    'mission.values.openness': '开放合作',
    'mission.values.openness.desc': '拥抱开源，促进行业协作发展',
    'mission.values.customer': '客户成功',
    'mission.values.customer.desc': '以客户成功为己任，创造长期价值',

    // Innovation Page
    'innovation.hero.title': '关键创新',
    'innovation.hero.subtitle': '引领汽车行业技术革新',
    'innovation.description': '我们在芯片级仿真和自动化设计方面拥有核心技术创新。',
    'innovation.research.title': '研发方向',
    'innovation.research.desc': '我们专注于汽车行业的前沿技术研发，致力于解决行业痛点，推动技术进步。',
    
    'innovation.areas.chip.title': '芯片级仿真技术',
    'innovation.areas.chip.desc': '开发L4级芯片行为模型，实现高精度仿真，支持复杂场景验证',
    'innovation.areas.cloud.title': '云原生开发',
    'innovation.areas.cloud.desc': '构建弹性可扩展的云平台，支持大规模协同开发',
    'innovation.areas.ai.title': '自动化设计',
    'innovation.areas.ai.desc': '集成机器学习算法，提供智能化的设计优化建议',
    'innovation.areas.automation.title': '自动化工具链',
    'innovation.areas.automation.desc': '打通从设计到部署的全流程自动化',



    // Contact Page
    'contact.hero.title': '联系我们',
    'contact.hero.subtitle': '我们很乐意听到您的声音。请通过以下方式与我们联系，或填写表单，我们会尽快回复您。',

    'contact.methods.title': '联系方式',
    'contact.methods.subtitle': '选择最适合您的联系方式，我们的团队随时为您提供支持',
    'contact.methods.email.title': '邮箱联系',
    'contact.methods.email.desc': '发送邮件获取详细信息',
    'contact.methods.phone.title': '电话联系',
    'contact.methods.phone.desc': '工作时间内即时沟通',
    'contact.methods.address.title': '公司地址',
    'contact.methods.address.details': '上海市徐汇区',
    'contact.methods.address.desc': '欢迎预约参观',
    'contact.methods.chat.title': '在线咨询',
    'contact.methods.chat.details': '实时技术支持',
    'contact.methods.chat.desc': '获取即时帮助',

    'contact.form.title': '发送消息',
    'contact.form.subtitle': '填写以下表单，我们会在24小时内回复您',
    'contact.form.name.label': '姓名',
    'contact.form.name.placeholder': '请输入您的姓名',
    'contact.form.email.label': '邮箱',
    'contact.form.email.placeholder': 'your.email@company.com',
    'contact.form.company.label': '公司名称',
    'contact.form.company.placeholder': '请输入公司名称（选填）',
    'contact.form.subject.label': '咨询类型',
    'contact.form.subject.placeholder': '请选择咨询类型',
    'contact.form.message.label': '详细信息',
    'contact.form.message.placeholder': '请详细描述您的需求或问题...',
    'contact.form.submit': '发送消息',

    'contact.form.types.product': '产品咨询',
    'contact.form.types.technical': '技术支持',
    'contact.form.types.business': '商务合作',
    'contact.form.types.media': '媒体合作',
    'contact.form.types.recruitment': '人才招聘',
    'contact.form.types.other': '其他',

    'contact.info.title': '联系信息',
    'contact.info.email.title': '邮箱地址',
    'contact.info.email.hr': 'hr@xdt.com (人力资源)',
    'contact.info.phone.title': '联系电话',
    'contact.info.phone.hours': '工作日 9:00-18:00',
    'contact.info.address.title': '办公地址',
    'contact.info.address.line1': '上海市徐汇区',
    'contact.info.address.line2': '',
    'contact.info.hours.title': '工作时间',
    'contact.info.hours.days': '周一至周五',
    'contact.info.hours.time': '上午 9:00 - 下午 6:00',

    'contact.faq.title': '常见问题',
    'contact.faq.subtitle': '快速找到您想了解的信息',
    'contact.faq.trial.question': '如何申请产品试用？',
    'contact.faq.trial.answer': '您可以通过联系表单申请试用，我们会在收到申请后48小时内安排专业团队与您对接，提供详细的产品演示和试用环境。',
    'contact.faq.tech.question': '技术支持服务时间？',
    'contact.faq.tech.answer': '我们提供7x24小时技术支持服务。工作日期间可通过电话、邮件或在线客服获得即时支持，非工作时间通过邮件联系，我们会在下个工作日及时回复。',
    'contact.faq.support.question': '支持哪些集成方式？',
    'contact.faq.support.answer': '平台支持多种集成方式，包括REST API、SDK、插件等。我们也支持与主流CAE软件（如CATIA、Siemens NX）和CI/CD工具的集成。',
    'contact.faq.cooperation.question': '如何开展商务合作？',
    'contact.faq.cooperation.answer': '如有商务合作意向，请通过商务合作类型的联系表单或直接致电我们的商务团队。我们会安排专业的商务经理与您详细沟通合作方案。',

    // Contact Form Email Template
    'contact.email.name': '姓名',
    'contact.email.company': '公司',
    'contact.email.email': '邮箱',
    'contact.email.message': '消息',

    // Footer Navigation
    'footer.company.about': '关于我们',
    'footer.company.vision': '公司愿景',
    'footer.company.mission': '公司使命',
    'footer.company.innovation': '关键创新',
    
    'footer.products.onesoul': 'One Soul 平台',
    'footer.products.onebody': 'One Body 平台',
    'footer.products.toolchain': '工具链软件',
    
    'footer.resources.docs': '技术文档',
    'footer.resources.community': '开发者社区',
    'footer.resources.api': 'API 文档',
    'footer.resources.demo': '在线演示',
    
    'footer.legal.privacy': '隐私政策',
    'footer.legal.terms': '服务条款',
    'footer.legal.legal': '法律声明',

    // Careers Page
    'careers.hero.title': '加入我们',
    'careers.hero.subtitle': '与我们一起塑造汽车行业的未来',
    'careers.hero.desc': '我们正在寻找有志于改变汽车行业的杰出人才，共同构建下一代智能汽车开发平台。',

    'careers.hero.cta.positions': '查看职位',
    'careers.hero.cta.apply': '提交简历',

    'careers.why.title': '为什么选择我们',
    'careers.why.subtitle': '在这里，您将获得无限的成长机会和广阔的发展空间',

    'careers.culture.innovation.title': '创新驱动',
    'careers.culture.innovation.desc': '鼓励创新思维，提供实现想法的平台和资源',
    'careers.culture.collaboration.title': '开放协作',
    'careers.culture.collaboration.desc': '崇尚开源精神，建立互相学习的团队环境',
    'careers.culture.growth.title': '持续成长',
    'careers.culture.growth.desc': '提供完善的培训体系和职业发展路径',
    'careers.culture.quality.title': '追求卓越',
    'careers.culture.quality.desc': '以高标准要求自己，打造世界级的产品',

    'careers.positions.title': '开放职位',
    'careers.positions.subtitle': '加入我们的团队，参与构建汽车行业的未来',

    'careers.labels.requirements': '职位要求',
    'careers.labels.apply': '申请此职位',
    'careers.labels.benefits': '职位福利',
    'careers.labels.competitive': '有竞争力的薪酬',
    'careers.labels.equity': '股权激励计划',
    'careers.labels.flexible': '灵活的工作方式',
    'careers.labels.training': '技术培训和会议',

    'careers.company.culture.title': '公司文化',
    'careers.company.culture.subtitle': '我们相信优秀的文化是创新的基础',
    'careers.company.innovation.title': '创新驱动',
    'careers.company.innovation.desc': '我们鼓励每个人提出创新想法，勇于尝试新技术和新方法。在这里，创新不是口号，而是我们的日常工作方式。',
    'careers.company.collaboration.title': '开放协作',
    'careers.company.collaboration.desc': '我们崇尚开源精神，相信开放协作能够创造更大价值。团队成员之间互相学习，共同成长。',
    'careers.company.learning.title': '持续学习',
    'careers.company.learning.desc': '技术快速发展，我们鼓励团队成员不断学习新知识，公司提供充分的学习资源和成长机会。',
    'careers.company.mission.title': '使命导向',
    'careers.company.mission.desc': '我们共同致力于推动汽车行业的数字化转型，每个人的工作都在为这个宏大目标做出贡献。',

    // Job Positions
    // Digital Twin Engineer
    'careers.positions.digital.title': '数字孪生工程师',
    'careers.positions.digital.department': '技术研发',
    'careers.positions.digital.location': '上海',
    'careers.positions.digital.type': '全职',
    'careers.positions.digital.description': '负责汽车数字孪生系统的设计和开发，构建高精度的虚拟车辆模型。',
    'careers.positions.digital.req.1': '计算机科学、软件工程或相关专业硕士及以上学历',
    'careers.positions.digital.req.2': '3年以上数字孪生或仿真系统开发经验',
    'careers.positions.digital.req.3': '熟悉C++、Python等编程语言',
    'careers.positions.digital.req.4': '了解汽车电子电气架构和控制系统',
    
    // Compiler Engineer
    'careers.positions.compiler.title': '编译器与建模工具开发工程师',
    'careers.positions.compiler.department': '工具链研发',
    'careers.positions.compiler.location': '上海',
    'careers.positions.compiler.type': '全职',
    'careers.positions.compiler.description': '开发L4级芯片行为模型和编译器工具，支持多架构平台的自动化代码生成。',
    'careers.positions.compiler.req.1': '计算机科学、软件工程专业本科及以上学历',
    'careers.positions.compiler.req.2': '5年以上编译器或建模工具开发经验',
    'careers.positions.compiler.req.3': '精通LLVM、GCC等编译器技术',
    'careers.positions.compiler.req.4': '熟悉ARM、RISC-V等芯片架构',
    
    // AI Engineer
    'careers.positions.ai.title': '自动化设计算法工程师',
    'careers.positions.ai.department': '自动化设计研发',
    'careers.positions.ai.location': '上海',
    'careers.positions.ai.type': '全职',
    'careers.positions.ai.description': '研发自动化设计驱动的汽车设计自动化算法，实现生成式设计和智能优化。',
    'careers.positions.ai.req.1': '人工智能、机器学习相关专业硕士及以上学历',
    'careers.positions.ai.req.2': '3年以上自动化设计算法研发经验',
    'careers.positions.ai.req.3': '熟悉深度学习框架（TensorFlow、PyTorch）',
    'careers.positions.ai.req.4': '有汽车行业应用经验者优先',
    
    // CAE Engineer
    'careers.positions.cae.title': 'CAE集成开发工程师',
    'careers.positions.cae.department': '平台集成',
    'careers.positions.cae.location': '上海',
    'careers.positions.cae.type': '全职',
    'careers.positions.cae.description': '负责CAE软件集成和汽车仿真平台开发，支持多种主流CAE工具。',
    'careers.positions.cae.req.1': '机械工程、汽车工程相关专业本科及以上学历',
    'careers.positions.cae.req.2': '熟悉CATIA、Siemens NX等CAE软件',
    'careers.positions.cae.req.3': '有汽车结构分析和仿真经验',
    'careers.positions.cae.req.4': '具备软件集成和API开发能力',
    
    // System Architect
    'careers.positions.architect.title': '汽车系统架构师',
    'careers.positions.architect.department': '系统设计',
    'careers.positions.architect.location': '上海',
    'careers.positions.architect.type': '全职',
    'careers.positions.architect.description': '设计整车电子电气架构，制定技术标准和开发规范。',
    'careers.positions.architect.req.1': '汽车工程、电子工程相关专业硕士及以上学历',
    'careers.positions.architect.req.2': '8年以上汽车电子电气架构设计经验',
    'careers.positions.architect.req.3': '深度了解汽车功能安全和网络安全',
    'careers.positions.architect.req.4': '具备团队管理和项目管理能力',

    'careers.cta.title': '准备好加入我们了吗？',
    'careers.cta.subtitle': '发送您的简历，或与我们的招聘团队取得联系',
    'careers.cta.contact': '联系招聘团队',
    'careers.cta.more': '了解更多',

    // Vision Page CTA
    'vision.cta.title': '与我们一起实现这个愿景',
    'vision.cta.subtitle': '加入xDT，成为汽车行业变革的推动者',
    'vision.cta.join': '加入我们',
    'vision.cta.learn': '了解更多',

    // Mission Page
    'mission.hero.content': '通过提供先进的开发工具和平台，推动汽车行业向智能化、电动化转型。',
    'mission.implementation.title': '实现路径',
    'mission.implementation.subtitle': '通过技术创新和开放协作，我们正在逐步实现这一使命',

    'mission.platform.title': '统一开发平台',
    'mission.platform.feature1': '基于L4级芯片行为模型的高精度仿真',
    'mission.platform.feature2': '云原生架构支持大规模协同开发',
    'mission.platform.feature3': '与主流工具链无缝集成',

    'mission.toolchain.title': '自动化设计驱动工具链',
    'mission.toolchain.feature1': '智能生成式设计，提升开发效率80%',
    'mission.toolchain.feature2': '自动化测试与验证流程',
    'mission.toolchain.feature3': '持续集成与部署（CI/CD）支持',

    'mission.ecosystem.title': '开放生态系统',
    'mission.ecosystem.feature1': '开发者社区与知识共享平台',
    'mission.ecosystem.feature2': '第三方工具与服务集成',
    'mission.ecosystem.feature3': '行业标准制定与推广',

    'mission.value.title': '我们的价值主张',
    'mission.value.subtitle': '通过技术创新为汽车行业创造持久价值',

    'mission.value.innovation.title': '技术创新',
    'mission.value.innovation.feature1': '引领行业技术发展趋势',
    'mission.value.innovation.feature2': '提供突破性的解决方案',
    'mission.value.innovation.feature3': '推动产业转型升级',

    'mission.value.efficiency.title': '效率提升',
    'mission.value.efficiency.feature1': '显著缩短产品开发周期',
    'mission.value.efficiency.feature2': '降低开发成本和风险',
    'mission.value.efficiency.feature3': '提高团队协作效率',

    'mission.cta.title': '加入我们的使命',
    'mission.cta.subtitle': '与我们一起推动汽车行业的数字化转型',
    'mission.cta.products': '了解产品',
    'mission.cta.careers': '加入团队',

    // Innovation Page
    'innovation.first.badge': '核心创新一',
    'innovation.first.title': 'L4级芯片行为模型仿真技术',

    'innovation.first.feature1.title': '高精度建模',
    'innovation.first.feature1.desc': '支持ARM、RISC-V等主流架构的精确建模',

    'innovation.first.feature2.title': '实时仿真',
    'innovation.first.feature2.desc': '毫秒级响应，支持复杂场景验证',

    'innovation.first.feature3.title': '可扩展性',
    'innovation.first.feature3.desc': '模块化设计，支持自定义扩展',

    'innovation.first.feature4.title': '工具集成',
    'innovation.first.feature4.desc': '与主流开发工具无缝对接',

    'innovation.first.visual.title': '核心技术指标',
    'innovation.first.visual.subtitle': '业界领先的仿真精度和性能表现',
    'innovation.first.visual.accuracy': '仿真精度',
    'innovation.first.visual.accuracy.value': '99.9%',
    'innovation.first.visual.coverage': '架构覆盖',
    'innovation.first.visual.coverage.value': '主流芯片',
    'innovation.first.visual.arch': '支持架构',
    'innovation.first.visual.arch.value': 'ARM/RISC-V',
    'innovation.first.visual.integration': '工具集成',
    'innovation.first.visual.integration.value': '20+工具',

    'innovation.second.badge': '核心创新二',
    'innovation.second.title': '自动化汽车设计平台',

    'innovation.second.feature1.title': '生成式设计',
    'innovation.second.feature1.desc': '基于自动化设计算法的智能设计方案生成',

    'innovation.second.feature2.title': '知识库驱动',
    'innovation.second.feature2.desc': '集成汽车工程领域专业知识',

    'innovation.second.feature3.title': '多目标优化',
    'innovation.second.feature3.desc': '同时优化性能、成本、安全等指标',

    'innovation.second.visual.title': '自动化设计能力',
    'innovation.second.visual.subtitle': '智能化设计工具显著提升设计效率',
    'innovation.second.visual.design': '设计方案',
    'innovation.second.visual.design.desc': '多种智能方案生成',
    'innovation.second.visual.knowledge': '专业知识',
    'innovation.second.visual.knowledge.desc': '汽车工程知识库',
    'innovation.second.visual.badge': '专业',

    'innovation.second.expansion.title': '应用领域扩展',
    'innovation.second.expansion.power': '动力系统',
    'innovation.second.expansion.chassis': '底盘系统',
    'innovation.second.expansion.body': '车身结构',
    'innovation.second.expansion.electrical': '电气系统',
    'innovation.second.expansion.interior': '内饰设计',
    'innovation.second.expansion.safety': '安全系统',

    'innovation.impact.title': '技术影响力',
    'innovation.impact.subtitle': '我们的创新技术正在改变汽车开发的方式',

    'innovation.impact.efficiency.title': '开发效率提升',
    'innovation.impact.efficiency.value': '80%',
    'innovation.impact.efficiency.desc': '相比传统开发方式',

    'innovation.impact.quality.title': '质量提升',
    'innovation.impact.quality.value': '95%',
    'innovation.impact.quality.desc': '缺陷检出率',

    'innovation.impact.ai.title': '自动化设计驱动优化',
    'innovation.impact.ai.value': '50+',
    'innovation.impact.ai.desc': '智能优化算法',

    'innovation.cta.title': '体验我们的创新技术',
    'innovation.cta.subtitle': '了解这些创新如何帮助您的团队实现突破',
    'innovation.cta.products': '产品体验',
    'innovation.cta.contact': '联系专家',


  },
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.about': 'About Us',
    'nav.vision': 'Company Vision',
    'nav.mission': 'Company Mission',
    'nav.innovation': 'Key Innovations',
    'nav.products': 'Main Products',
    'nav.scenarios': 'Application Scenarios',
    'nav.careers': 'Careers',
    'nav.contact': 'Contact',

    // Home Page
    'home.hero.badge': 'Future-Oriented Smart Vehicle Development Platform',
    'home.hero.title': 'xDT',
    'home.hero.subtitle': 'GitHub for Automotive Industry',
    'home.hero.description': 'Collaborative development platform for future smart electric vehicles',
    'home.hero.detail': 'Through cloud services and digital twin technology, we create an open, collaborative integrated development environment to help global developers efficiently build next-generation smart electric vehicles.',
    'home.hero.cta1': 'Explore Our Platform',
    'home.hero.cta2': 'Learn About Our Products',
    'home.hero.cta3': 'Contact Us',

    // Home hero visual elements
    'home.hero.visual.title1': 'Vehicle Virtualization Development',
    'home.hero.visual.desc1': 'Complete virtualization solution based on L4-level chip behavioral models',
    'home.hero.visual.title2': 'Automated Design',
    'home.hero.visual.desc2': 'Intelligent generative design tools improving efficiency by 80%',
    'home.hero.visual.title3': 'Cloud-Native Platform',
    'home.hero.visual.desc3': 'Cloud-based IDE supporting CI/CD workflows',

    'home.features.title': 'Why Choose xDT?',
    'home.features.subtitle': 'We provide industry-leading smart electric vehicle development solutions to help your team achieve breakthrough innovations',

    'home.feature.simulation.title': 'High-Precision Simulation Models',
    'home.feature.simulation.desc': 'Based on L4-level chip behavioral models, supporting mainstream architectures like ARM and RISC-V, enabling realistic controller and vehicle simulation.',

    'home.feature.collaboration.title': 'Open Collaboration Platform',
    'home.feature.collaboration.desc': 'GitHub-like open ecosystem supporting global developers to collaborate and innovate on a unified platform, promoting knowledge sharing.',

    'home.feature.ai.title': 'Automated Design',
    'home.feature.ai.desc': 'Integrating automated design with automotive subsystem knowledge for generative design and optimization, reducing development time by 80%.',

    'home.feature.cloud.title': 'Cloud-Native Development',
    'home.feature.cloud.desc': 'Providing cloud-based integrated development environment with CI/CD support for shift-left development and automation enhancement.',

    'home.feature.vehicle.title': 'Vehicle-Level Support',
    'home.feature.vehicle.desc': 'Complete toolchain from chips to vehicles, covering electrical/electronic architecture design, simulation, and testing.',

    'home.feature.industrial.title': 'Industrial-Grade Reliability',
    'home.feature.industrial.desc': 'Mature solutions validated by automotive companies, meeting strict quality and safety standards of the automotive industry.',

    // Home CTA section
    'home.cta.title': 'Ready to Start Your Smart Vehicle Development Journey?',
    'home.cta.subtitle': 'Join xDT and build the future of automobiles with global developers',
    'home.cta.cta1': 'Explore Our Products',
    'home.cta.cta2': 'Watch Product Demo',

    // Company info
    'company.name': 'xDT',
    'company.tagline': 'GitHub for Automotive Industry',
    'company.description': 'GitHub for automotive industry, a collaborative development platform for future smart electric vehicles. Through cloud services and digital twin technology, we create an open, collaborative integrated development environment.',
    'company.location': 'Shanghai Xuhui, China',
    'company.email': 'contact@xdt.com',
    'company.copyright': 'All rights reserved.',
    'company.slogan': 'Industrial Software · GitHub for Automotive · Smart Electric Vehicle Collaborative Development Platform',

    // Footer
    'footer.company': 'Company',
    'footer.products': 'Products',
    'footer.resources': 'Developer Resources',
    'footer.legal': 'Legal',

    // About Page
    'about.hero.title': 'About xDT',
    'about.hero.subtitle': 'GitHub for Automotive Industry',
    'about.hero.description': 'We are committed to becoming the GitHub of the automotive industry, providing efficient collaborative development solutions for smart electric vehicle developers',
    
    'about.intro.title': 'Company Introduction',
    'about.intro.positioning': 'Industry Positioning:',
    'about.intro.positioning.desc': 'xDT focuses on industrial software, providing innovative technical solutions for the smart electric vehicle industry.',
    'about.intro.purpose': 'Founding Purpose:',
    'about.intro.purpose.desc': 'As the automotive industry transforms toward "software-defined vehicles," we recognize the need for an open, collaborative development platform. Just as GitHub changed software development, we aim to bring the same transformation to the automotive industry.',
    'about.intro.goal': 'Development Goals:',
    'about.intro.goal.desc': 'To become the GitHub of the automotive industry, building a global developer ecosystem that drives smart vehicle software innovation, enabling every developer to collaborate efficiently on our platform and jointly build the future of automobiles.',
    
    'about.tech.title': 'Technical Background',
    'about.tech.intro': 'Our core technology is based on L4-level chip behavioral model high-precision simulation systems, representing industry-leading technological innovation. Through deep integration of cutting-edge technologies such as digital twins, cloud-native, and automated toolchains, we provide automotive developers with unprecedented development experiences.',
    'about.tech.digital.title': 'Digital Twin Technology',
    'about.tech.digital.desc': 'Build high-fidelity virtual vehicle models, achieving precise mapping between physical and digital worlds, supporting lifecycle simulation and verification.',
    'about.tech.cloud.title': 'Cloud-Native Architecture',
    'about.tech.cloud.desc': 'Kubernetes-based elastic architecture supporting large-scale concurrent development with anytime, anywhere development environment access.',
    'about.tech.automation.title': 'Automated Toolchain',
    'about.tech.automation.desc': 'Fully automated workflow from code commit to deployment, supporting CI/CD to significantly improve development efficiency and code quality.',
    'about.tech.ai.title': 'Automated Design Optimization',
    'about.tech.ai.desc': 'Integrated machine learning algorithms providing intelligent design recommendations and performance optimization solutions to accelerate innovation.',
    
    'about.team.title': 'Team Introduction',
    'about.team.intro': 'Our core team consists of senior experts from the automotive and software industries, with rich cross-industry experience and deep technical expertise.',
    'about.team.tech.title': 'Technical Experts',
    'about.team.tech.desc': 'Deep expertise in automotive electronics, chip design, system architecture and other core technical fields',
    'about.team.product.title': 'Product Experts',
    'about.team.product.desc': 'Rich experience in B2B product design and user experience optimization',
    'about.team.industry.title': 'Industry Experts',
    'about.team.industry.desc': 'Senior engineers from leading automotive OEMs and Tier1 suppliers',
    
    'about.features.industrial': 'Industrial Software',
    'about.features.industrial.desc': 'Professional Domain',
    'about.features.smart': 'Smart Vehicles',
    'about.features.smart.desc': 'Service Target',
    'about.features.collaboration': 'Collaborative Development',
    'about.features.collaboration.desc': 'Core Philosophy',
    'about.features.innovation': 'Innovation Leadership',
    'about.features.innovation.desc': 'Development Direction',

    // Products Page
    'products.hero.title': 'Our Products',
    'products.hero.subtitle': 'Complete Smart Electric Vehicle Development Toolchain',
    'products.hero.description': 'From chip-level simulation to vehicle development, we provide comprehensive solutions to help you build next-generation smart electric vehicles.',

    'products.onesoul.title': 'One Soul Platform',
    'products.onesoul.subtitle': 'Vehicle EE Architecture Virtualization Development Platform',
    'products.onesoul.badge': '✅ Platform Products',
    'products.onesoul.features.title': 'Key Features:',
    'products.onesoul.features.1': 'L4-level chip behavioral model library (covering ARM/RISC-V, etc.)',
    'products.onesoul.features.2': 'Vehicle EE architecture reference design and model library',
    'products.onesoul.features.3': 'Support for in-house/third-party tool integration',
    'products.onesoul.features.4': 'Seamless integration with CI/CD workflows',
    'products.onesoul.scenarios.title': 'Application Scenarios:',
    'products.onesoul.scenarios.1': 'Architecture Selection Validation',
    'products.onesoul.scenarios.2': 'Virtualized Development & Testing',
    'products.onesoul.scenarios.3': 'Automated Regression Testing',
    
    'products.onebody.title': 'One Body Platform',
    'products.onebody.subtitle': 'Automated Automotive Design Platform',
    'products.onebody.badge': '✅ Platform Products',
    'products.onebody.features.title': 'Key Features:',
    'products.onebody.features.1': 'Automated design-based generative design and optimization tools',
    'products.onebody.features.2': 'Domain knowledge base + training models support lightweight design',
    'products.onebody.features.3': 'Lightweight IDE compatible with mainstream CAE software (such as CATIA, Siemens NX, etc.)',
    'products.onebody.scenarios.title': 'Application Scenarios:',
    'products.onebody.scenarios.1': 'Wiring Harness Optimization',
    'products.onebody.scenarios.2': 'Rapid Door Design',
    'products.onebody.scenarios.3': 'Structural Performance Optimization',
    
    // Products Visual Cards
    'products.onesoul.visual.title': 'L4-Level Chip Simulation',
    'products.onesoul.visual.subtitle': 'High-precision behavioral simulation supporting mainstream architectures like ARM and RISC-V',
    'products.onesoul.visual.arch': 'Architecture Support',
    'products.onesoul.visual.arch.value': 'ARM/RISC-V',
    'products.onesoul.visual.os': 'Operating System',
    'products.onesoul.visual.os.value': 'POSIX Compatible',
    
    'products.onebody.visual.title': 'Automated Design',
    'products.onebody.visual.subtitle': 'Automated design-powered generative design tools with 80% development efficiency improvement',
    'products.onebody.visual.wiring': 'Wiring Optimization',
    'products.onebody.visual.wiring.desc': 'Auto-generate optimal paths',
    'products.onebody.visual.design': 'Component Design',
    'products.onebody.visual.design.desc': 'Rapid model generation',
    
    // Products CTA
    'products.cta.title': 'Want to Experience Our Products?',
    'products.cta.subtitle': 'Contact us for product demonstrations and technical support',
    'products.cta.contact': 'Contact Us',
    'products.cta.demo': 'Schedule Demo',
    
    'products.toolchain.badge': 'Toolchain Software',
    'products.toolchain.title': 'Complete Toolchain Ecosystem',
    'products.toolchain.subtitle': 'Complete toolchain from chip models to vehicle design, covering the entire smart electric vehicle development process',
    'products.toolchain.table.name': 'Tool Name',
    'products.toolchain.table.description': 'Function Description',
    
    // Toolchain tools
    'products.tools.1.name': 'L4-level Chip Behavioral Model Development Tool',
    'products.tools.1.desc': 'Achieve high-precision chip-level simulation',
    'products.tools.2.name': 'L4-level Chip Behavioral Model Library',
    'products.tools.2.desc': 'Model resources covering mainstream chip architectures',
    'products.tools.3.name': 'Reference SWC Software Library',
    'products.tools.3.desc': 'Standardized software component library',
    'products.tools.4.name': 'Autocoding Tool',
    'products.tools.4.desc': 'Enable cross-chip platform automatic adaptation and compilation',
    'products.tools.5.name': 'Controller Power Consumption Simulation Tool',
    'products.tools.5.desc': 'Simulate power performance in real usage scenarios',
    'products.tools.6.name': 'Automotive Wiring Harness 3D Automated Routing Software',
    'products.tools.6.desc': 'Automatically generate optimal vehicle wiring harness paths and models',
    'products.tools.7.name': 'Automotive Door Automated Design Software',
    'products.tools.7.desc': 'Rapidly generate engineering-standard door models based on A-surface',
    'products.tools.8.name': 'Body-in-White Upper Body Automated Design Software',
    'products.tools.8.desc': 'Rapidly generate engineering-standard body-in-white upper body models based on A-surface',
    'products.tools.9.name': 'Battery Pack Automated Design Software',
    'products.tools.9.desc': 'Automatically generate battery pack design and models',

    // Use Cases Section
    'products.usecases.title': 'Use Cases',
    'products.usecases.subtitle': 'Real customer cases demonstrating platform value',
    'products.usecases.oem.title': 'How OEMs Use One Soul Platform for Early Architecture Validation',
    'products.usecases.oem.desc': 'Through L4-level chip behavioral models, OEMs can complete comprehensive EE architecture validation before hardware production, reducing development risks and shortening R&D cycles by over 30%.',
    'products.usecases.oem.benefits.1': 'Early detection of architecture design issues',
    'products.usecases.oem.benefits.2': 'Reduced hardware development costs',
    'products.usecases.oem.benefits.3': 'Accelerated time-to-market',
    
    'products.usecases.tier1.title': 'How Tier1 Suppliers Accelerate Component Design Iteration with One Body Platform',
    'products.usecases.tier1.desc': 'Based on automated design tools, Tier1 suppliers can rapidly generate multiple design solutions, achieving 80% improvement in design iteration efficiency while ensuring engineering standard compliance.',
    'products.usecases.tier1.benefits.1': '80% improvement in design efficiency',
    'products.usecases.tier1.benefits.2': 'Automated quality inspection',
    'products.usecases.tier1.benefits.3': 'Rapid multi-solution comparison',
    
    'products.usecases.autonomous.title': 'How Autonomous Driving Companies Integrate Our Toolchain for Rapid Deployment',
    'products.usecases.autonomous.desc': 'Through our complete toolchain ecosystem, autonomous driving companies can achieve full-process development from chip-level simulation to vehicle validation, implementing CI/CD automated deployment and significantly improving development efficiency.',
    'products.usecases.autonomous.benefits.1': 'End-to-end automated development',
    'products.usecases.autonomous.benefits.2': 'CI/CD integrated deployment',
    'products.usecases.autonomous.benefits.3': 'Cross-platform compatibility validation',

    // Scenarios Page - Case Studies
    'scenarios.casestudies.title': 'Customer Success Stories',
    'scenarios.casestudies.subtitle': 'Real cases demonstrating how our platform helps different types of customers solve actual business challenges',
    'scenarios.oem.badge': 'OEM Case',
    'scenarios.oem.case.title': 'Leading OEM EE Architecture Validation Project',
    'scenarios.oem.case.desc': 'This OEM used our One Soul platform for early EE architecture validation in next-generation smart vehicle development, successfully identifying and resolving 12 critical architecture issues before hardware production, avoiding potential losses exceeding 20 million yuan.',
    'scenarios.oem.metrics.cycle': 'R&D Cycle Reduction',
    'scenarios.oem.metrics.issues': 'Issues Found Early',
    'scenarios.oem.progress.title': 'Project Progress',
    'scenarios.oem.progress.ecu': 'ECU Nodes',
    'scenarios.oem.progress.tests': 'Test Cases',
    
    'scenarios.tier1.badge': 'Tier1 Case',
    'scenarios.tier1.case.title': 'Wiring Harness Supplier Design Optimization Project',
    'scenarios.tier1.case.desc': 'This Tier1 supplier used our One Body platform in a luxury brand vehicle wiring harness design project, reducing the original 30-day design cycle to 6 days while generating 5 different optimization solutions for customer selection.',
    'scenarios.tier1.metrics.efficiency': 'Design Efficiency Improvement',
    'scenarios.tier1.traditional': 'Traditional Design',
    'scenarios.tier1.ai': 'Automated Design',
    'scenarios.tier1.benefits.routing': 'Automated routing path optimization',
    'scenarios.tier1.benefits.comparison': 'Rapid multi-solution generation and comparison',
    'scenarios.tier1.benefits.validation': 'Automated engineering standard validation',
    
    'scenarios.cta.title': 'Want to Learn More Use Cases?',
    'scenarios.cta.subtitle': 'Contact our expert team for detailed case analysis and customized solutions',
    'scenarios.cta.contact': 'Contact Expert',
    'scenarios.cta.products': 'Learn Products',

    // Vision Page
    'vision.hero.title': 'Company Vision',
    'vision.hero.subtitle': 'Becoming the GitHub of Automotive Industry',
    'vision.content': 'Our vision is to become the GitHub of the automotive industry, creating the world\'s largest open-source collaboration platform for smart electric vehicles.',

    // Vision Goals Section
    'vision.goals.title': 'Specific Goals',
    'vision.goals.subtitle': 'Through three core objectives, we will realize our vision of becoming the GitHub of the automotive industry',
    
    'vision.goals.ecosystem.title': 'Build an Open, Transparent, and Collaborative Development Ecosystem',
    'vision.goals.ecosystem.desc': 'Create a platform as open and transparent as GitHub, enabling global automotive developers to freely share code, tools, and knowledge, fostering collaborative innovation across the industry.',
    
    'vision.goals.collaboration.title': 'Support Global Developers to Collaborate and Innovate on a Unified Platform',
    'vision.goals.collaboration.desc': 'Provide unified development environments and collaboration tools, enabling developers from different countries and companies to collaborate seamlessly and jointly advance smart vehicle technology.',
    
    'vision.goals.transformation.title': 'Drive Automotive Industry Transformation from "Hardware-Driven" to "Software-Defined"',
    'vision.goals.transformation.desc': 'By providing advanced software development tools and platforms, accelerate the automotive industry\'s transformation toward software-defined vehicles, making software the core driver of automotive innovation.',

    // Vision Impact Section
    'vision.impact.title': 'Impact of Vision Realization',
    'vision.impact.subtitle': 'When our vision is realized, it will bring profound changes to the entire automotive industry',
    
    'vision.impact.developers.title': 'Impact on Developers',
    'vision.impact.developers.1': 'Lower barriers to automotive software development, enabling more developers to participate in innovation',
    'vision.impact.developers.2': 'Provide rich open-source tools and resource libraries to accelerate development processes',
    'vision.impact.developers.3': 'Establish a global developer community promoting knowledge sharing and learning',
    
    'vision.impact.industry.title': 'Impact on Industry',
    'vision.impact.industry.1': 'Accelerate the digital transformation process of the entire automotive industry',
    'vision.impact.industry.2': 'Promote standardization and interoperability, reducing redundant development',
    'vision.impact.industry.3': 'Foster innovative collaboration, shortening time to market',

    // Mission Page
    'mission.hero.title': 'Company Mission',
    'mission.hero.subtitle': 'Driving Digital Transformation in Automotive Industry',
    'mission.content': 'To drive the transformation of the automotive industry towards intelligence and electrification by providing advanced development tools and platforms.',
    'mission.our.title': 'Our Mission',
    'mission.our.desc': 'Drive digital transformation in the automotive industry, enabling every developer to enjoy high-quality development tools and platform services.',
    'mission.goals.1': 'Lower automotive development barriers, enabling more innovators to participate in smart vehicle development',
    'mission.goals.2': 'Provide world-class development tools and platforms to accelerate technological progress in the automotive industry',
    'mission.goals.3': 'Build an open collaborative ecosystem, promoting knowledge sharing and cooperation within and beyond the industry',
    
    'mission.values.title': 'Core Values',
    'mission.values.innovation': 'Innovation Driven',
    'mission.values.innovation.desc': 'Continuous innovation, leading technology trends',
    'mission.values.quality': 'Quality First',
    'mission.values.quality.desc': 'Pursue ultimate product quality and user experience',
    'mission.values.openness': 'Open Collaboration',
    'mission.values.openness.desc': 'Embrace open source, promote industry collaborative development',
    'mission.values.customer': 'Customer Success',
    'mission.values.customer.desc': 'Committed to customer success, creating long-term value',

    // Innovation Page
    'innovation.hero.title': 'Key Innovations',
    'innovation.hero.subtitle': 'Leading Technological Innovation in Automotive Industry',
    'innovation.description': 'We have core technological innovations in chip-level simulation and automated design.',
    'innovation.research.title': 'Research Directions',
    'innovation.research.desc': 'We focus on cutting-edge technology research in the automotive industry, committed to solving industry pain points and driving technological progress.',
    
    'innovation.areas.chip.title': 'Chip-Level Simulation Technology',
    'innovation.areas.chip.desc': 'Develop L4-level chip behavioral models for high-precision simulation and complex scenario validation',
    'innovation.areas.cloud.title': 'Cloud-Native Development',
    'innovation.areas.cloud.desc': 'Build elastic and scalable cloud platforms supporting large-scale collaborative development',
    'innovation.areas.ai.title': 'Automated Design',
    'innovation.areas.ai.desc': 'Integrate machine learning algorithms to provide intelligent design optimization recommendations',
    'innovation.areas.automation.title': 'Automated Toolchain',
    'innovation.areas.automation.desc': 'Enable end-to-end automation from design to deployment',



    // Contact Page
    'contact.hero.title': 'Contact Us',
    'contact.hero.subtitle': 'We\'d love to hear from you. Get in touch with us through any of the following methods, or fill out the form and we\'ll get back to you as soon as possible.',

    'contact.methods.title': 'Contact Methods',
    'contact.methods.subtitle': 'Choose the contact method that works best for you, our team is ready to provide support',
    'contact.methods.email.title': 'Email Contact',
    'contact.methods.email.desc': 'Send email for detailed information',
    'contact.methods.phone.title': 'Phone Contact',
    'contact.methods.phone.desc': 'Instant communication during business hours',
    'contact.methods.address.title': 'Office Address',
    'contact.methods.address.details': 'Xuhui District, Shanghai',
    'contact.methods.address.desc': 'Welcome to schedule a visit',
    'contact.methods.chat.title': 'Online Consultation',
    'contact.methods.chat.details': 'Real-time technical support',
    'contact.methods.chat.desc': 'Get instant help',

    'contact.form.title': 'Send Message',
    'contact.form.subtitle': 'Fill out the form below and we\'ll get back to you within 24 hours',
    'contact.form.name.label': 'Name',
    'contact.form.name.placeholder': 'Please enter your name',
    'contact.form.email.label': 'Email',
    'contact.form.email.placeholder': 'your.email@company.com',
    'contact.form.company.label': 'Company Name',
    'contact.form.company.placeholder': 'Enter company name (optional)',
    'contact.form.subject.label': 'Inquiry Type',
    'contact.form.subject.placeholder': 'Please select inquiry type',
    'contact.form.message.label': 'Details',
    'contact.form.message.placeholder': 'Please describe your requirements or questions in detail...',
    'contact.form.submit': 'Send Message',

    'contact.form.types.product': 'Product Inquiry',
    'contact.form.types.technical': 'Technical Cooperation',
    'contact.form.types.business': 'Business Partnership',
    'contact.form.types.media': 'Media Partnership',
    'contact.form.types.recruitment': 'Recruitment',
    'contact.form.types.other': 'Other',
    
    'contact.info.title': 'Contact Information',
    'contact.info.email.title': 'Email Address',
    'contact.info.email.hr': 'hr@xdt.com (Human Resources)',
    'contact.info.phone.title': 'Phone Number',
    'contact.info.phone.hours': 'Weekdays 9:00-18:00',
    'contact.info.address.title': 'Office Address',
    'contact.info.address.line1': 'Xuhui District, Shanghai',
    'contact.info.address.line2': '',
    'contact.info.hours.title': 'Business Hours',
    'contact.info.hours.days': 'Monday to Friday',
    'contact.info.hours.time': '9:00 AM - 6:00 PM',

    'contact.faq.title': 'Frequently Asked Questions',
    'contact.faq.subtitle': 'Quickly find the information you need',
    'contact.faq.trial.question': 'How to apply for product trial?',
    'contact.faq.trial.answer': 'You can apply for a trial through the contact form. We will arrange a professional team to connect with you within 48 hours after receiving your application, providing detailed product demonstrations and trial environments.',
    'contact.faq.tech.question': 'Technical support service hours?',
    'contact.faq.tech.answer': 'We provide 7x24 hour technical support services. During business days, you can get instant support via phone, email, or online customer service. For non-business hours, contact us via email and we will respond promptly on the next business day.',
    'contact.faq.support.question': 'What integration methods are supported?',
    'contact.faq.support.answer': 'The platform supports multiple integration methods, including REST API, SDK, plugins, etc. We also support integration with mainstream CAE software (such as CATIA, Siemens NX) and CI/CD tools.',
    'contact.faq.cooperation.question': 'How to establish business cooperation?',
    'contact.faq.cooperation.answer': 'If you have business cooperation intentions, please contact us through the business cooperation type contact form or call our business team directly. We will arrange a professional business manager to discuss cooperation plans with you in detail.',

    // Contact Form Email Template
    'contact.email.name': 'Name',
    'contact.email.company': 'Company',
    'contact.email.email': 'Email',
    'contact.email.message': 'Message',

    // Footer Navigation
    'footer.company.about': 'About Us',
    'footer.company.vision': 'Company Vision',
    'footer.company.mission': 'Company Mission',
    'footer.company.innovation': 'Key Innovations',
    
    'footer.products.onesoul': 'One Soul Platform',
    'footer.products.onebody': 'One Body Platform',
    'footer.products.toolchain': 'Toolchain Software',
    
    'footer.resources.docs': 'Technical Documentation',
    'footer.resources.community': 'Developer Community',
    'footer.resources.api': 'API Documentation',
    'footer.resources.demo': 'Online Demo',
    
    'footer.legal.privacy': 'Privacy Policy',
    'footer.legal.terms': 'Terms of Service',
    'footer.legal.legal': 'Legal Notice',

    // Careers Page
    'careers.hero.title': 'Join Us',
    'careers.hero.subtitle': 'Shape the Future of Automotive Industry Together',
    'careers.hero.desc': 'We are looking for outstanding talents who aspire to change the automotive industry and build the next-generation smart vehicle development platform together.',

    'careers.hero.cta.positions': 'View Positions',
    'careers.hero.cta.apply': 'Submit Resume',

    'careers.why.title': 'Why Choose Us',
    'careers.why.subtitle': 'Here, you will gain unlimited growth opportunities and broad development space',

    'careers.culture.innovation.title': 'Innovation Driven',
    'careers.culture.innovation.desc': 'Encourage innovative thinking and provide platforms and resources to realize ideas',
    'careers.culture.collaboration.title': 'Open Collaboration',
    'careers.culture.collaboration.desc': 'Embrace open source spirit and build a team environment for mutual learning',
    'careers.culture.growth.title': 'Continuous Growth',
    'careers.culture.growth.desc': 'Provide comprehensive training systems and career development paths',
    'careers.culture.quality.title': 'Pursue Excellence',
    'careers.culture.quality.desc': 'Hold ourselves to high standards and build world-class products',

    'careers.positions.title': 'Open Positions',
    'careers.positions.subtitle': 'Join our team and participate in building the future of the automotive industry',

    'careers.labels.requirements': 'Requirements',
    'careers.labels.apply': 'Apply for Position',
    'careers.labels.benefits': 'Position Benefits',
    'careers.labels.competitive': 'Competitive Salary',
    'careers.labels.equity': 'Equity Incentive Plan',
    'careers.labels.flexible': 'Flexible Work Style',
    'careers.labels.training': 'Technical Training and Conferences',

    'careers.company.culture.title': 'Company Culture',
    'careers.company.culture.subtitle': 'We believe excellent culture is the foundation of innovation',
    'careers.company.innovation.title': 'Innovation Driven',
    'careers.company.innovation.desc': 'We encourage everyone to propose innovative ideas and dare to try new technologies and methods. Here, innovation is not a slogan, but our daily work style.',
    'careers.company.collaboration.title': 'Open Collaboration',
    'careers.company.collaboration.desc': 'We advocate the open source spirit and believe that open collaboration can create greater value. Team members learn from each other and grow together.',
    'careers.company.learning.title': 'Continuous Learning',
    'careers.company.learning.desc': 'With rapid technological development, we encourage team members to continuously learn new knowledge. The company provides ample learning resources and growth opportunities.',
    'careers.company.mission.title': 'Mission Oriented',
    'careers.company.mission.desc': 'We are committed to driving digital transformation in the automotive industry. Everyone\'s work contributes to this grand goal.',

    // Job Positions
    // Digital Twin Engineer
    'careers.positions.digital.title': 'Digital Twin Engineer',
    'careers.positions.digital.department': 'Technology R&D',
    'careers.positions.digital.location': 'Shanghai',
    'careers.positions.digital.type': 'Full-time',
    'careers.positions.digital.description': 'Responsible for the design and development of automotive digital twin systems, building high-precision virtual vehicle models.',
    'careers.positions.digital.req.1': 'Master\'s degree or above in Computer Science, Software Engineering or related fields',
    'careers.positions.digital.req.2': '3+ years of experience in digital twin or simulation system development',
    'careers.positions.digital.req.3': 'Proficient in C++, Python and other programming languages',
    'careers.positions.digital.req.4': 'Understanding of automotive electronic/electrical architecture and control systems',
    
    // Compiler Engineer
    'careers.positions.compiler.title': 'Compiler & Modeling Tools Development Engineer',
    'careers.positions.compiler.department': 'Toolchain R&D',
    'careers.positions.compiler.location': 'Shanghai',
    'careers.positions.compiler.type': 'Full-time',
    'careers.positions.compiler.description': 'Develop L4-level chip behavior models and compiler tools, supporting automated code generation across multiple architecture platforms.',
    'careers.positions.compiler.req.1': 'Bachelor\'s degree or above in Computer Science, Software Engineering',
    'careers.positions.compiler.req.2': '5+ years of experience in compiler or modeling tool development',
    'careers.positions.compiler.req.3': 'Proficient in LLVM, GCC and other compiler technologies',
    'careers.positions.compiler.req.4': 'Familiar with ARM, RISC-V and other chip architectures',
    
    // AI Engineer
    'careers.positions.ai.title': 'Automated Design Algorithm Engineer',
    'careers.positions.ai.department': 'Automated Design R&D',
    'careers.positions.ai.location': 'Shanghai',
    'careers.positions.ai.type': 'Full-time',
    'careers.positions.ai.description': 'Research and develop automated design-driven automotive design automation algorithms, achieving generative design and intelligent optimization.',
    'careers.positions.ai.req.1': 'Master\'s degree or above in Artificial Intelligence, Machine Learning or related fields',
    'careers.positions.ai.req.2': '3+ years of experience in automated design algorithm research and development',
    'careers.positions.ai.req.3': 'Familiar with deep learning frameworks (TensorFlow, PyTorch)',
    'careers.positions.ai.req.4': 'Experience in automotive industry applications preferred',
    
    // CAE Engineer
    'careers.positions.cae.title': 'CAE Integration Development Engineer',
    'careers.positions.cae.department': 'Platform Integration',
    'careers.positions.cae.location': 'Shanghai',
    'careers.positions.cae.type': 'Full-time',
    'careers.positions.cae.description': 'Responsible for CAE software integration and automotive simulation platform development, supporting various mainstream CAE tools.',
    'careers.positions.cae.req.1': 'Bachelor\'s degree or above in Mechanical Engineering, Automotive Engineering or related fields',
    'careers.positions.cae.req.2': 'Familiar with CATIA, Siemens NX and other CAE software',
    'careers.positions.cae.req.3': 'Experience in automotive structural analysis and simulation',
    'careers.positions.cae.req.4': 'Software integration and API development capabilities',
    
    // System Architect
    'careers.positions.architect.title': 'Automotive System Architect',
    'careers.positions.architect.department': 'System Design',
    'careers.positions.architect.location': 'Shanghai',
    'careers.positions.architect.type': 'Full-time',
    'careers.positions.architect.description': 'Design vehicle electronic/electrical architecture, formulate technical standards and development specifications.',
    'careers.positions.architect.req.1': 'Master\'s degree or above in Automotive Engineering, Electronic Engineering or related fields',
    'careers.positions.architect.req.2': '8+ years of experience in automotive electronic/electrical architecture design',
    'careers.positions.architect.req.3': 'Deep understanding of automotive functional safety and cybersecurity',
    'careers.positions.architect.req.4': 'Team management and project management capabilities',

    'careers.cta.title': 'Ready to Join Us?',
    'careers.cta.subtitle': 'Send your resume or get in touch with our recruitment team',
    'careers.cta.contact': 'Contact Recruitment Team',
    'careers.cta.more': 'Learn More',

    // Vision Page CTA
    'vision.cta.title': 'Realize This Vision Together',
    'vision.cta.subtitle': 'Join xDT and become a driver of change in the automotive industry',
    'vision.cta.join': 'Join Us',
    'vision.cta.learn': 'Learn More',

    // Mission Page
    'mission.hero.content': 'To drive the transformation of the automotive industry towards intelligence and electrification by providing advanced development tools and platforms.',
    'mission.implementation.title': 'Implementation Path',
    'mission.implementation.subtitle': 'Through technological innovation and open collaboration, we are gradually realizing this mission',

    'mission.platform.title': 'Unified Development Platform',
    'mission.platform.feature1': 'High-precision simulation based on L4-level chip behavioral models',
    'mission.platform.feature2': 'Cloud-native architecture supporting large-scale collaborative development',
    'mission.platform.feature3': 'Seamless integration with mainstream toolchains',

    'mission.toolchain.title': 'Automated Design-Driven Toolchain',
    'mission.toolchain.feature1': 'Intelligent generative design improving efficiency by 80%',
    'mission.toolchain.feature2': 'Automated testing and validation processes',
    'mission.toolchain.feature3': 'Continuous Integration and Deployment (CI/CD) support',

    'mission.ecosystem.title': 'Open Ecosystem',
    'mission.ecosystem.feature1': 'Developer community and knowledge sharing platform',
    'mission.ecosystem.feature2': 'Third-party tools and services integration',
    'mission.ecosystem.feature3': 'Industry standard development and promotion',

    'mission.value.title': 'Our Value Proposition',
    'mission.value.subtitle': 'Creating lasting value for the automotive industry through technological innovation',

    'mission.value.innovation.title': 'Technological Innovation',
    'mission.value.innovation.feature1': 'Leading industry technology development trends',
    'mission.value.innovation.feature2': 'Providing breakthrough solutions',
    'mission.value.innovation.feature3': 'Driving industrial transformation and upgrading',

    'mission.value.efficiency.title': 'Efficiency Enhancement',
    'mission.value.efficiency.feature1': 'Significantly reducing product development cycles',
    'mission.value.efficiency.feature2': 'Lowering development costs and risks',
    'mission.value.efficiency.feature3': 'Improving team collaboration efficiency',

    'mission.cta.title': 'Join Our Mission',
    'mission.cta.subtitle': 'Work with us to drive digital transformation in the automotive industry',
    'mission.cta.products': 'Learn Products',
    'mission.cta.careers': 'Join Team',

    // Innovation Page
    'innovation.first.badge': 'Core Innovation I',
    'innovation.first.title': 'L4-Level Chip Behavioral Model Simulation Technology',

    'innovation.first.feature1.title': 'High-Precision Modeling',
    'innovation.first.feature1.desc': 'Precise modeling support for mainstream architectures like ARM and RISC-V',

    'innovation.first.feature2.title': 'Real-Time Simulation',
    'innovation.first.feature2.desc': 'Millisecond-level response supporting complex scenario validation',

    'innovation.first.feature3.title': 'Scalability',
    'innovation.first.feature3.desc': 'Modular design supporting custom extensions',

    'innovation.first.feature4.title': 'Tool Integration',
    'innovation.first.feature4.desc': 'Seamless integration with mainstream development tools',

    'innovation.first.visual.title': 'Core Technical Metrics',
    'innovation.first.visual.subtitle': 'Industry-leading simulation accuracy and performance',
    'innovation.first.visual.accuracy': 'Simulation Accuracy',
    'innovation.first.visual.accuracy.value': '99.9%',
    'innovation.first.visual.coverage': 'Architecture Coverage',
    'innovation.first.visual.coverage.value': 'Mainstream Chips',
    'innovation.first.visual.arch': 'Supported Architectures',
    'innovation.first.visual.arch.value': 'ARM/RISC-V',
    'innovation.first.visual.integration': 'Tool Integration',
    'innovation.first.visual.integration.value': '20+ Tools',

    'innovation.second.badge': 'Core Innovation II',
    'innovation.second.title': 'Automated Automotive Design Platform',

    'innovation.second.feature1.title': 'Generative Design',
    'innovation.second.feature1.desc': 'Automated design algorithm-based intelligent design solution generation',

    'innovation.second.feature2.title': 'Knowledge Base Driven',
    'innovation.second.feature2.desc': 'Integrated automotive engineering domain expertise',

    'innovation.second.feature3.title': 'Multi-Objective Optimization',
    'innovation.second.feature3.desc': 'Simultaneous optimization of performance, cost, safety and other metrics',

    'innovation.second.visual.title': 'Automated Design Capabilities',
    'innovation.second.visual.subtitle': 'Intelligent design tools significantly improve design efficiency',
    'innovation.second.visual.design': 'Design Solutions',
    'innovation.second.visual.design.desc': 'Multiple intelligent solution generation',
    'innovation.second.visual.knowledge': 'Professional Knowledge',
    'innovation.second.visual.knowledge.desc': 'Automotive engineering knowledge base',
    'innovation.second.visual.badge': 'Expert',

    'innovation.second.expansion.title': 'Application Domain Expansion',
    'innovation.second.expansion.power': 'Powertrain System',
    'innovation.second.expansion.chassis': 'Chassis System',
    'innovation.second.expansion.body': 'Body Structure',
    'innovation.second.expansion.electrical': 'Electrical System',
    'innovation.second.expansion.interior': 'Interior Design',
    'innovation.second.expansion.safety': 'Safety System',

    'innovation.impact.title': 'Technical Impact',
    'innovation.impact.subtitle': 'Our innovative technologies are changing the way automotive development works',

    'innovation.impact.efficiency.title': 'Development Efficiency Improvement',
    'innovation.impact.efficiency.value': '80%',
    'innovation.impact.efficiency.desc': 'Compared to traditional development methods',

    'innovation.impact.quality.title': 'Quality Improvement',
    'innovation.impact.quality.value': '95%',
    'innovation.impact.quality.desc': 'Defect detection rate',

    'innovation.impact.ai.title': 'Automated Design-Driven Optimization',
    'innovation.impact.ai.value': '50+',
    'innovation.impact.ai.desc': 'Intelligent optimization algorithms',

    'innovation.cta.title': 'Experience Our Innovative Technology',
    'innovation.cta.subtitle': 'Learn how these innovations can help your team achieve breakthroughs',
    'innovation.cta.products': 'Product Experience',
    'innovation.cta.contact': 'Contact Expert',


  }
} 